import { backendUrl, language, rootDomain } from "../util/config";
import * as Sentry from "@sentry/browser";
import { Cart, LineItem } from "@/types/types";
import { ClientsideCart } from "@/types/clientSideCart";
import { getUrlParameters } from "./getUrlParameters";
import { isPrintType, FrameColor, isFramed, isFrameable, PrintDelivery } from "@/types/print";
import { ICookiesController } from "@/types/cookiesController";
import getCheckout from "./getCheckout";
import putItem from "./putItem";
import fireAddToCartEvent from "@/util/fireAddToCartEvent";
import { currency } from "@/util/getCurrency";
import { 
  printTypeToShopifyVariantName,
  productTypeToShopifyProductName,
  productTypeToShopifyProductTitle
} from "../types/ga4Tracking";
const cartBaseUrl = backendUrl;
let cartProvided: ClientsideCart = undefined;
let cookiesController: ICookiesController = undefined;
let removedAllItemsFromCart = false;

export async function initialize(cartData: ClientsideCart) {
  if (!cartData) {
    throw new Error("Cart Data may not be undefined!");
  }

  cartProvided = cartData;
  cookiesController = cartData.$cookies;

  Sentry.setExtra("url-href", location.href);
  Sentry.setExtra("url-search", location.search);
  const urlParameters = getUrlParameters();

  Sentry.setExtra("url-params", urlParameters);
  if (urlParameters.productId) {
    Sentry.setExtra("put-item", "yes");
    let printType = urlParameters.printType;
    // Backward compability
    if (!printType && urlParameters.lineItemProperties) {
      printType = urlParameters.lineItemProperties.delivery;
    }
    if (printType == "pdf") {
      printType = "digital";
    }
    if (!isPrintType(printType)) {
      throw new Error("Unknown print type: " + printType);
    }
    let printProperties = undefined;
    if (urlParameters.frameColor) {

      printProperties = { frameColor: urlParameters.frameColor }
    }

    const cartFromServer = await putItem(
      urlParameters.productId,
      currency,
      urlParameters.productType,
      printType,
      printProperties,
      urlParameters.replaceId,
    );

    // remove url parameters for adding product from url, so reload wont readd a cart item
    if (history.replaceState) {
      history.replaceState(null, "", "?");
    }
    if (cartFromServer.lineItems.length == 0) {
      // If this error is thrown im changing professions
      throw new Error(
        "received empty cart from server, although we sent something there.",
      );
    }
    updateLocalCartFromServerResponse(cartFromServer);
    const addedLineItem = cartFromServer.lineItems.find(
      (lineItem) => lineItem.product.productId === urlParameters.productId,
    );
    fireAddToCartEvent(addedLineItem);
  } else {
    Sentry.setExtra("put_item", "no");
    await refreshCart();
  }
  refreshCheckoutURL();

  Sentry.setExtra("referrer-href", document.referrer);
  if (
    cartProvided.lineItems.length == 0 && 
    !removedAllItemsFromCart &&
    document.referrer.includes("editor")
  ) {
    const win: any = window;
    if (win.gtag) {
      win.gtag("event", "Problem", {
        event_label: "Empty Cart",
        event_category: "Empty Cart",
        non_interaction: true,
      });
    }
    throw new Error("Editor visitor received empty cart!");
  }
}

export async function refreshCart() {
  // we add the current time to prevent the request from being cached and therefore showing stale data
  const getCartResponse = await fetch(
    cartBaseUrl + "/cart/" + language + "/" + currency + "?t=" + Date.now(),
    {
      method: "GET",
      credentials: "include",
    },
  );
  const cartFromServer = await getCartResponse.json();

  updateLocalCartFromServerResponse(cartFromServer);
}

export async function refreshCheckoutURL() {
  // setting checkoutURL to undefined because we don't want the user to checkout with a stale cart while the update loads
  cartProvided.checkoutURL = undefined;
  if (cartProvided.lineItemsLoaded && !cartProvided.lineItems.length) {
    return;
  }
  const promises = [getCheckout(currency).then((url) => (cartProvided.checkoutURL = url))]
  await Promise.all(promises);
}

function updateCookiesDate(cartFromServer: ClientsideCart) {
  const numberOfLineItems = cartFromServer.lineItems.length || 0;
  // set the key-value cookies for the root domain
  // this will allow us to share it with our subdomains
  cookiesController.set(
    "numberOfItemsInCart",
    numberOfLineItems,
    "30d",
    "/",
    rootDomain,
  );
}
export async function deleteItemfromCart(productId: string) {
  await fetch(`${cartBaseUrl}/cart/${productId}`, {
    method: "DELETE",
    credentials: "include",
  });
  await refreshCart();
  refreshCheckoutURL();
  removedAllItemsFromCart = cartProvided.lineItems.length === 0
}

export async function updateItem(newItem: LineItem) {
  const newCart = await putItem(
    newItem.product.productId,
    currency,
    newItem.product.type,
    newItem.print.type,
    newItem.print.printProperties,
  );
  updateLocalCartFromServerResponse(newCart);
  refreshCheckoutURL();
}

export async function frame(productId: string, frameColor: FrameColor) {
  const item = cartProvided.lineItems.find(
    (l) => l.product.productId == productId,
  );

  if (
    !isFramed(item.print.frameProperties) &&
    !isFrameable(item.print.frameProperties)
  ) {
    throw new Error("Print type is not frameable or framed");
  }

  if (isFrameable(item.print.frameProperties)) {
    item.print.type = item.print.frameProperties.framedVersion;
  }
  if (item.print.printProperties == undefined) {
    item.print.printProperties = {} as any;
  }
  item.print.printProperties.frameColor = frameColor;
  await updateItem(item);
}

export async function unframe(productId: string) {
  const item = cartProvided.lineItems.find(
    (l) => l.product.productId == productId,
  );
  if (!isFramed(item.print.frameProperties)) {
    throw new Error("Print type is not unframeable");
  }
  item.print.type = item.print.frameProperties.unframedVersion;
  // might need to change in the future
  item.print.printProperties = undefined;
  updateItem(item);
}

function updateLocalCartFromServerResponse(cartFromServer: Cart) {
  Sentry.setExtra("server-response", cartFromServer);
  cartProvided.lineItems = cartFromServer.lineItems;
  cartProvided.lineItemsLoaded = true;
  cartProvided.deliveryHighlighting = cartFromServer.deliveryHighlighting;
  cartProvided.deliveryExpectation = cartFromServer.deliveryExpectation || "";

  cartProvided.madeIn = cartFromServer.madeIn || "";
  cartProvided.isEmpty = cartProvided.lineItems.length === 0;
  updateCookiesDate(cartProvided);
  fireGA4ViewCartEvent(cartFromServer.lineItems);
}

function fireGA4ViewCartEvent(lineItems: LineItem[]) {
  const price = lineItems.reduce((sum, item) => sum + item.price, 0);
  const items = lineItems.map((lineItem) => ({
    item_id: productTypeToShopifyProductTitle[lineItem.product.type],
    item_name: productTypeToShopifyProductName[lineItem.product.type],
    item_brand: "Heureka Prints",
    item_variant: printTypeToShopifyVariantName[lineItem.print.type],
    price: lineItem.price,
    quantity: 1,
  }));

  if (window.dataLayer) {
    window.dataLayer.push({
      event: "view_cart",
      value: price,
      currency: currency,
      ecommerce: {
        items,
      },
    });
  }
}
